import { reelsToRows } from "../../Utils.js";
import { NonStopWebSocket } from "./NonStopWebSocket.js";
import { decodeIfBase64 } from "onearm";

const ERROR_COMMAND_NOT_SUPPORTED = {"libs":2,"error":"Command not supported"}

export class RemoteAPI {
    /**
     * @type {any}
     */
    gameMath = null;

    /**
     * @type {NonStopWebSocket}
     */
    webSocket = null;

    constructor(gameMath, gameConfig) {
        this.gameMath = gameMath;
        this.paramters = new URLSearchParams(window.location.search);
        this.game = gameConfig.gameId || "dhm1";

        let ws = "";
        if (  this.paramters.has("ws") ) {
            try {
                const decode = decodeIfBase64(this.paramters.get("ws"));
                if (decode.ok) {
                    ws = decode.value;
                } else {
                    ws = this.paramters.get("ws");
                }
            } catch (e) {
                this.error = e;
                return;
            }
        }

        try {
            this.session = this.paramters.get("session");
            if (!this.session) {
                if (!this.session) {
                    this.session = this.randomSession();
                }
            }

            this.webSocket = new NonStopWebSocket(ws);
            this.cheat = null;
        } catch (error) {
            this.error = error;
        }
    }
    // get base 64 string
    randomSession(length = 10) {
        const chars = "0123456789abcdefghijklmnopqrstuvwxyz";
        let result = "";
        for (let i = 0; i < length; i++) {
            result += chars[Math.floor(Math.random() * chars.length)];
        }
        return result;
    }

    async connect() {
        return this.webSocket.connect();
    }

    async authentication() {
        return this.webSocket.set("authentication", {
            location: window.location,
            cookie: document.cookie,
            key: this.session,
        });
    }

    async game() {
        return this.webSocket.set("game", { game: this.game });
    }

    async bet(bet) {
        return this.webSocket.set("bet", { bet_sum: bet });
    }

    async payTable(options = {}) {
        return this.webSocket.get("paytable", options);
    }

    async setOptions(options) {
        return this.webSocket.set("options", options);
    }

    async getOptions() {
        return this.webSocket.get("options");
    }

    async setBet(bet) {
        const { options } = await this.setOptions({ bet_sum: bet });

        if (!options) {
            return { error: "Options not found" };
        }

        return options;
    }

    async player() {
        if (this.error) {
            return { error: this.error.message || "Unknown error" };
        }
        try {
            await this.connect();
            const { authentication, set_cookie } = await this.authentication();

            if (!authentication) {
                return { error: "Authentication" };
            }

            if (set_cookie) {
                for (let i = 0; i < set_cookie.length; i++) {
                    document.cookie = set_cookie[i];
                }
            }

            const {
                game,
                bets,
                previous = {},
                wallet,
            } = await this.webSocket.set("game", { game: this.game });

            if (!game) {
                return { error: "Game not found" };
            }

            const { options } = await this.getOptions();

            if (!options) {
                return { error: "Options not found" };
            }

            const { bet_sum, feature } = Object.assign({}, options, previous);
            const { bonus, bigwin_level } = game;
            const bonuses = bonus
                ? Object.keys(bonus).map(key => ({ id: key, ...bonus[key] }))
                : [];
            const reels = this.gameMath.generateReelsWithAllSymbols();
            const lastSpin = this.gameMath.rowsToData(reels);
            const { freespin, total, counter } = previous;
            let freespins = null;

            if (freespin && !freespin.last) {
                freespins = {
                    left: freespin.limit - freespin.counter,
                    current: freespin.counter,
                    total: freespin.limit,
                    totalWin: freespin.win,
                    betLevel: freespin.bet,
                };
            }

            const playerData = {
                feature,
                session: "",
                bet: bet_sum,
                bets: bets,
                bonuses,
                balance: wallet.balance,
                currency: wallet.currency,
                lastSpin: previous ? this.convertReels(previous.reels || []) : [],
                bigwinLevels: bigwin_level,
                freespins,
            };

            return playerData;
        } catch (error) {
            return { error: "Internal error" };
        }
    }

    async buyFreeSpins(betAmount, id) {
        const { bet, wallet } = await this.webSocket.set("bet", {
            bet_sum: betAmount,
            buy_bonus: id,
        });

        if (!bet) {
            return { error: "Buy free spins error" };
        }

        const { total, freespin, counter } = bet;
        const result = {
            freespins: {
                left: freespin.limit - freespin.counter,
                current: freespin.counter,
                total: freespin.limit,
                totalWin: total.win,
                betLevel: freespin.bet,
            },
            spin: [this.betToSpin(bet)],
            total_win: total.win,
            after_balance: wallet.balance,
        };

        return result;
    }

    async spin({ bet: betAmount } = {}) {
        if (this.cheat) {
            await this.setCheat(this.cheat);
            // this.cheat = null;
        }

        let { bet, wallet } = await this.bet(betAmount);

        if (!bet) {
            return { error: "Bet error" };
        }
        const { total, freespin } = bet;

        const freespins = freespin
            ? {
                left: freespin.limit - freespin.counter,
                current: freespin.counter,
                total: freespin.limit,
                totalWin: freespin.win,
                betLevel: freespin.bet,
            }
            : null;
        const winBeforPay =  0;

        return {
            freespins,
            spin: [this.betToSpin(bet, winBeforPay)],
            total_win: total.win,
            after_balance: wallet.balance,
        };
    }

    setCheat(cheat) {
        return this.webSocket.set("cheat", { cheat });
    }

    async freeSpin(freeSpins) {
        const result = await this.spin({ bet: freeSpins.betLevel });

        return result;
    }


    async getPayTable() {
        const { paytable } = await this.payTable();

        if (!paytable) {
            return { error: "Paytable not found" };
        }

        return paytable.paytable;
    }

    async setDoubleChanceFeature({isEnabled}) {
        const { options } = await this.setOptions({ feature: {toggle: isEnabled} });

        if (!options) {
            return { error: "Options not found" };
        }

        return options;
    }

    /**
     * @description Converts bet response to sequence of animation steps
     * @param {Object} bet - Bet response from server
     * @param {number} winBeforPay - Win before pay
     * @returns {Array} Array of animation steps
     */
    betToSpin(bet, winBeforPay) {
        const { total = { win: 0 }, win = [], reels = [], final_multiplier, freespin } = bet;
        const steps = [];
        let currentReelsState = reels;
        let lastDestroyPositions = null;
        let accumulatedWin = 0;
        let cascadeNumber = 0;
        const isRetrigger = freespin && freespin.counter > 0;
        steps.push({
            type: "stop",
            reels: reels,
        });

        let i = 0;
        while (i < win.length) {
            const step = win[i];
            const { type, state } = step;

            if (type === "regular") {
                const regularSteps = [];
                while (i < win.length && win[i].type === "regular") {
                    regularSteps.push(win[i]);
                    i++;
                }

                const payStep = this.winStepsToMergedPay(regularSteps, winBeforPay + accumulatedWin);
                steps.push(payStep);
                accumulatedWin += payStep.win;

                const destroyStep = this.winStepsToMergedDestroy(regularSteps);
                steps.push(destroyStep);
                lastDestroyPositions = destroyStep.positions;
            } else if (type === "scatter") {
                const scatterStep = this.winStepToScatter(step, isRetrigger);
                steps.push(scatterStep);
                i++;
            } else if (type === "reels" && state === "tumble") {
                currentReelsState = step.reels;
                i++;
            } else if (type === "reels" && state === "new") {
                const cascadeStep = this.winStepToCascade(step, lastDestroyPositions);
                steps.push({...cascadeStep, number: cascadeNumber});
                cascadeNumber++;
                currentReelsState = step.reels;
                lastDestroyPositions = null;
                i++;
            } else {
                i++;
            }
        }

        if (final_multiplier && win.length > 0) {
            const multiplierStep = this.getMultiplierStep(currentReelsState, final_multiplier, accumulatedWin, total.win, winBeforPay + accumulatedWin);
            if (multiplierStep) {
                steps.push(multiplierStep);
            }
        }

        const scatterStepIndex = steps.findIndex(step => step.type === "freeSpinsRetrigger" || step.type === "freeSpinsTrigger");
        if (scatterStepIndex !== -1) {
            const [scatterStep] = steps.splice(scatterStepIndex, 1);
            steps.push(scatterStep);
        }

        return steps;
    }

    /**
     * @description Converts single win step to pay data object
     * @param {Object} step - Win step from server
     * @returns {Object} Pay data
     */
    winStepToPayData(step) {
        const { reels, win, multiplier, bigwin, id } = step;
        const positions = this.winReelsToPositions(reels);

        return {
            win,
            bigWinLevel: bigwin ? bigwin.index : null,
            symbolId: id,
            multiplier,
            symbol: this.gameMath.symbolIdToName(id),
            symbols: positions.length,
            positions,
        };
    }

    /**
     * @description Merges multiple regular steps into single pay step with pays array
     * @param {Array} regularSteps - Array of regular win steps
     * @param {number}
     * @returns {Object} Merged pay step
     */
    winStepsToMergedPay(regularSteps, winBeforPay) {
        const pays = regularSteps.map(step => this.winStepToPayData(step));
        const totalWin = pays.reduce((sum, p) => sum + p.win, 0);
        const maxBigWinLevel = Math.max(...pays.map(p => p.bigWinLevel ?? -1));

        return {
            type: "pay",
            win: totalWin,
            winBeforPay,
            winAfterPay: winBeforPay + totalWin,
            bigWinLevel: maxBigWinLevel >= 0 ? maxBigWinLevel : null,
            pays,
        };
    }

    /**
     * @description Merges multiple regular steps into single destroy step
     * @param {Array} regularSteps - Array of regular win steps
     * @returns {Object} Merged destroy step
     */
    winStepsToMergedDestroy(regularSteps) {
        const allPositions = [];
        const positionSet = new Set();

        regularSteps.forEach(step => {
            const positions = this.winReelsToPositions(step.reels);
            positions.forEach(pos => {
                const key = `${pos.column},${pos.row}`;
                if (!positionSet.has(key)) {
                    positionSet.add(key);
                    allPositions.push(pos);
                }
            });
        });

        return {
            type: "destroy",
            positions: allPositions,
        };
    }

    /**
     * @description Converts scatter win step to scatter animation data
     * @param {Object} step - Scatter win step from server
     * @returns {Object} Scatter step data with positions and award
     */
    winStepToScatter(step, isRetrigger = false) {
        const { reels, win, multiplier, award, id } = step;
        const positions = this.winReelsToPositions(reels);

        return {
            type: isRetrigger ? "freeSpinsRetrigger" : "freeSpinsTrigger",
            win,
            multiplier,
            symbols: positions.length,
            positions,
            award: award || null,
            symbol: "S1"
        };
    }

    /**
     * @description Extracts multiplier symbols positions from reels
     * @param {Array} reels - Current reels state
     * @param {number} finalMultiplier - Total multiplier value
     * @param {number} totalWin - Total win before multiplier applied
     * @param {number} winBeforPay - Win before multiplier applied
     * @returns {Object|null} Multiplier step data or null if no multipliers
     */
    getMultiplierStep(reels, finalMultiplier, winBeforeMultiplier, totalWin, winBeforPay) {
        const multipliers = [];

        for (let column = 0; column < reels.length; column++) {
            for (let row = 0; row < reels[column].length; row++) {
                const symbol = reels[column][row];
                if (symbol && typeof symbol === "object" && symbol.multiplier) {
                    multipliers.push({
                        row,
                        column,
                        multiplier: symbol.multiplier,
                    });
                }
            }
        }

        if (multipliers.length === 0) {
            return null;
        }

        return {
            type: "multiplier",
            multipliers,
            multiplier: finalMultiplier,
            win: {
                total: totalWin,
                beforeMultiplier: winBeforeMultiplier,
                winAfterPay: winBeforPay + totalWin,
                winBeforPay: winBeforPay,
            }
        };
    }

    /**
     * @description Converts reels state to cascade animation data
     * @param {Object} newStep - New reels state step
     * @param {Array<{column: number, row: number}>} destroyedPositions - Positions that were destroyed
     * @returns {Object} Cascade step data
     */
    winStepToCascade(newStep, destroyedPositions) {
        const { reels: newReels } = newStep;
        const movements = {};
        const newSymbols = {};
        const columns = newReels.length;
        const rows = newReels[0].length;

        const destroyedByColumn = {};
        if (destroyedPositions) {
            destroyedPositions.forEach(({ column, row }) => {
                if (!destroyedByColumn[column]) {
                    destroyedByColumn[column] = [];
                }
                destroyedByColumn[column].push(row);
            });
        }

        for (let col = 0; col < columns; col++) {
            movements[col] = [];
            newSymbols[col] = [];

            const destroyedRows = destroyedByColumn[col] || [];
            if (destroyedRows.length === 0) continue;

            destroyedRows.sort((a, b) => a - b);

            let targetRow = rows - 1;
            for (let row = rows - 1; row >= 0; row--) {
                if (!destroyedRows.includes(row)) {
                    if (row !== targetRow) {
                        movements[col].push({
                            fromRow: row,
                            toRow: targetRow,
                        });
                    }
                    targetRow--;
                }
            }

            for (let i = 0; i <= targetRow; i++) {
                const symbolValue = newReels[col][i];
                newSymbols[col].push({
                    row: i,
                    symbolData: this.gameMath.getSymbolData(symbolValue),
                });
            }
        }

        return {
            type: "cascade",
            movements,
            newSymbols,
        };
    }

    symbolIDToName(id) {
        return this.gameConfig.symbols.find(symbol => symbol.id === id).name;
    }

    winToFreeSpinsAward(wins = [], spinReels, isRetrigger = false) {
        const freeSpinsAward = wins[0];

        if (freeSpinsAward) {
            const { reels, id, multiplier, award } = freeSpinsAward;
            const positions = this.winReelsToPositions(reels);


            return {
                type: isRetrigger ? "freeSpinsRetrigger" : "freeSpinsWin",
                multiplier,
                id,
                award,
                symbol: this.gameMath.symbolIdToName(this.getPaySymbol(spinReels, positions)),
                positions,
                symbols: positions.length,
            };
        }

        return null;
    }

    winToPays(wins = [], spinReels) {
        return wins.map(({ reels, win, type, bigwin, multiplier }) => {
            const positions = this.winReelsToPositions(reels);

            return {
                win,
                type,
                multiplier,
                bigWinLevel: bigwin ? bigwin.index : null,
                multiplierByPositions: this.winReelsToMultiplierByPositions(reels),
                multipliers: this.multipliersFromReels(reels),
                symbol: this.gameMath.symbolIdToName(this.getPaySymbol(spinReels, positions)),
                symbols: positions.length,
                positions: this.winReelsToPositions(reels),
            };
        });
    }

    getPaySymbol(reels, positions) {
        for (let i = 0; i < positions.length; i++) {
            const { column, row } = positions[i];
            const symbol = reels[row][column];

            if (!this.symbolHasMultiplier(symbol)) {
                return symbol;
            }
        }
    }

    symbolHasMultiplier(symbol) {
        return symbol && typeof symbol === "object" && symbol.multiplier;
    }

    winReelsToMultiplierByPositions(reels) {
        const multiplierByPositions = [];

        for (let column = 0; column < reels.length; column++) {
            for (let row = 0; row < reels[column].length; row++) {
                const reel = reels[column][row];

                if (reel && typeof reel === "object" && reel.multiplier) {
                    multiplierByPositions.push({
                        column,
                        row,
                        multiplier: reel.multiplier,
                        symbol: reel.id,
                    });
                }
            }
        }

        return multiplierByPositions;
    }

    multipliersFromReels(reels) {
        let multipliers = 0;

        for (let column = 0; column < reels.length; column++) {
            for (let row = 0; row < reels[column].length; row++) {
                const reel = reels[column][row];

                if (reel && typeof reel === "object" && reel.multiplier) {
                    multipliers += reel.multiplier;
                }
            }
        }

        return multipliers;
    }

    winReelsToPositions(reels) {
        const positions = [];

        for (let column = 0; column < reels.length; column++) {
            for (let row = 0; row < reels[column].length; row++) {
                if (reels[column][row]) {
                    positions.push({ column, row });
                }
            }
        }

        return positions;
    }

    convertReels(reels) {
        const rows = reelsToRows(reels);

        return this.gameMath.rowsToData(rows);
    }

    /**
     * Закрытие WebSocket соединения
     */
    close() {
        this.webSocket.close();
    }


}
